import React from "react";
import {
  Button,
  Input,
  message,
  Modal,
  Radio,
  Space,
  Typography,
  Upload,
} from "antd";
import { useTranslation } from "react-i18next";
import FileUploadImage from "../../../assets/img/fileUpload.png";
import {
  FILE_ACCEPT,
  MAX_MB,
  isSupportedExternalSource,
} from "../utils/dashboardUpload.utils";
import { AddDataChoice } from "../../../types/types";

const { Paragraph } = Typography;
const { Dragger } = Upload;

type Props = {
  open: boolean;
  addDataChoice: AddDataChoice;
  selectedFile: File | null;
  linkValue: string;
  onClose: () => void;
  onChoiceChange: (value: AddDataChoice) => void;
  onLinkChange: (value: string) => void;
  onFileChange: (file: File | null) => void;
  onContinue: () => void;
  submitting?: boolean;
};

const AddDataModal: React.FC<Props> = ({
  open,
  addDataChoice,
  selectedFile,
  linkValue,
  onClose,
  onChoiceChange,
  onLinkChange,
  onFileChange,
  onContinue,
  submitting = false,
}) => {
  const { t } = useTranslation("dashboardModals");
  const showExternalError =
    addDataChoice === "external" &&
    linkValue.trim().length > 0 &&
    !isSupportedExternalSource(linkValue);

  return (
    <Modal
      title={t("addDataModal.title")}
      open={open}
      className="adddata-todashboard-modal"
      onCancel={onClose}
      footer={[
        <Button key="cancel" className="cancelBbtn" onClick={onClose}>
          {t("addDataModal.cancel")}
        </Button>,
        <Button
          key="continue"
          onClick={onContinue}
          className="continue-btn"
          loading={submitting}
          disabled={
            (addDataChoice === "external" &&
              !isSupportedExternalSource(linkValue)) ||
            (addDataChoice === "internal" && !selectedFile)
          }
        >
          {t("addDataModal.continue")}
        </Button>,
      ]}
      destroyOnClose
    >
      <Paragraph className="modal-desc">
        {addDataChoice === "external"
          ? t("addDataModal.externalDesc")
          : t("addDataModal.internalDesc")}
      </Paragraph>

      <Radio.Group
        value={addDataChoice}
        onChange={(e) => {
          onChoiceChange(e.target.value);
        }}
        style={{ width: "100%" }}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <div style={{ width: "100%" }}>
            <Radio value="external">{t("addDataModal.externalOption")}</Radio>

            {addDataChoice === "external" && (
              <>
                <div style={{ fontWeight: 600, margin: "12px 0 8px" }}>
                  {t("addDataModal.pasteLinkLabel")}
                </div>

                <Input
                  placeholder={t("addDataModal.linkPlaceholder")}
                  value={linkValue}
                  onChange={(e) => onLinkChange(e.target.value)}
                  style={{ width: "100%" }}
                />

                <div
                  style={{
                    marginTop: 8,
                    fontSize: 13,
                    color: "#777",
                    lineHeight: 1.5,
                  }}
                >
                  {t("addDataModal.supportedExamples")}
                  <br />• {t("addDataModal.csvLinkExample")}
                  <br />• {t("addDataModal.xlsLinkExample")}
                </div>

                {showExternalError && (
                  <div
                    style={{
                      marginTop: 8,
                      fontSize: 13,
                      color: "#ff4d4f",
                    }}
                  >
                    {t("addDataModal.invalidLinkError")}
                  </div>
                )}
              </>
            )}
          </div>

          <div style={{ width: "100%" }}>
            <Radio value="internal">{t("addDataModal.internalOption")}</Radio>

            {addDataChoice === "internal" && (
              <>
                <div style={{ fontWeight: 600, margin: "12px 0 8px" }}>
                  {t("addDataModal.uploadDocumentLabel")}
                </div>

                <Dragger
                  multiple={false}
                  maxCount={1}
                  accept={FILE_ACCEPT}
                  beforeUpload={(file: File) => {
                    const allowedExtensions = FILE_ACCEPT.split(",").map((s) =>
                      s.trim().toLowerCase(),
                    );

                    const ext =
                      "." + (file.name.split(".").pop() || "").toLowerCase();

                    if (!allowedExtensions.includes(ext)) {
                      message.error(t("addDataModal.invalidFileTypeError"));
                      return Upload.LIST_IGNORE;
                    }

                    if (file.size > MAX_MB * 1024 * 1024) {
                      message.error(
                        t("addDataModal.maxFileSizeError", { maxMb: MAX_MB }),
                      );
                      return Upload.LIST_IGNORE;
                    }

                    onFileChange(file);
                    return false;
                  }}
                  onRemove={() => onFileChange(null)}
                  className="purple-dragger"
                  style={{
                    width: "100%",
                    borderRadius: 12,
                    border: "2px dashed #F4D9FF",
                    background: "#FAEEFF",
                  }}
                >
                  <div style={{ padding: 24, textAlign: "center" }}>
                    <img
                      className="img-style"
                      src={FileUploadImage}
                      alt="upload file"
                    />

                    <h3
                      style={{
                        color: "#A072EB",
                        fontSize: 16,
                        fontWeight: 600,
                      }}
                    >
                      {t("addDataModal.dragDropText")}{" "}
                      <u>{t("addDataModal.clickToSelectFiles")}</u>
                    </h3>

                    <div
                      style={{
                        opacity: 0.8,
                        fontSize: 14,
                        fontWeight: 600,
                        color: "#777777",
                      }}
                    >
                      {t("addDataModal.formatsList")}
                    </div>

                    <div
                      style={{
                        opacity: 0.6,
                        fontSize: 14,
                        color: "#777777",
                        marginTop: 8,
                      }}
                    >
                      {t("addDataModal.maxFileSizeLabel", { maxMb: MAX_MB })}
                    </div>
                  </div>
                </Dragger>
              </>
            )}
          </div>
        </Space>
      </Radio.Group>
    </Modal>
  );
};

export default AddDataModal;
